import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const About = () => {
  const { t, language, getLocalizedField } = useLanguage();
  const [siteContent, setSiteContent] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contentRes, galleryRes] = await Promise.all([
        axios.get(`${API}/content`),
        axios.get(`${API}/gallery`)
      ]);
      setSiteContent(contentRes.data);
      // Get 4 dish images for the grid
      const dishes = galleryRes.data?.filter(img => img.category === 'dishes').slice(0, 4) || [];
      setGalleryImages(dishes);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Default images as fallback
  const heroImage = siteContent?.about_image_2 || 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=80';
  const historyImage = siteContent?.hero_image || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80';
  const chefImage = siteContent?.chef_image || 'https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?w=800&q=80';
  
  const defaultGalleryImages = [
    'https://images.unsplash.com/photo-1544025162-d76694265947?w=400&q=80',
    'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=400&q=80',
    'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=80',
    'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?w=400&q=80'
  ];

  // Read text from siteContent with fallback defaults
  const fallback = {
    fr: {
      about_subtitle_short: 'Découvrez BÉNI',
      about_title: 'Notre Histoire',
      about_intro: 'Nous sommes un restaurant créé pour les vrais amateurs de bonne cuisine !',
      about_history_title: "L'Histoire de BÉNI",
      about_history_p1: "BÉNI est né de la passion de créer des expériences gastronomiques uniques.",
      about_history_p2: "Nous avons préparé un menu spécial pour ceux qui sont passionnés par la vraie cuisine !",
      about_chef_title: 'Derrière la Gastronomie',
      about_text: 'a le défi de ravir les palais.',
      about_quote: '"Cuisiner, c\'est tisser un manteau d\'arômes."',
      about_philosophy_title: 'Notre Philosophie',
      about_philosophy_text: 'Chez BÉNI, chaque plat raconte une histoire.',
      about_cta: 'Voir Notre Menu',
      chef_intro: 'Plus de 11 ans de cuisine professionnelle de haut niveau et une passion pour la cuisine et la pêche,',
      chef_text: 'a le défi de ravir les palais avec légèreté, qualité et amour. Une gastronomie axée sur le plaisir, mais aussi sur le réconfort de votre appétit.'
    },
    en: {
      chef_intro: 'Over 11 years of high-level professional cuisine and a passion for cooking and fishing,',
      chef_text: 'has the challenge of delighting palates with lightness, quality and love. A gastronomy focused on pleasing, but also on embracing your appetite.'
    },
    pt: {
      chef_intro: 'Mais de 11 anos de cozinha profissional de alto nível e paixão por cozinhar e pescar,',
      chef_text: 'tem o desafio de encantar paladares com leveza, qualidade e amor. Uma gastronomia focada em agradar, mas também em abraçar seu apetite.'
    }
  };

  const get = (field) => {
    if (!siteContent) return fallback.fr[field] || '';
    return getLocalizedField(siteContent, field) || fallback.fr[field] || '';
  };

  const chefName = siteContent?.chef_name || 'Stephano Crupi';
  // chef_intro/chef_text use translations fallback only (not in siteContent yet)
  const chefIntro = fallback[language]?.chef_intro || fallback.fr.chef_intro;
  const chefText = fallback[language]?.chef_text || fallback.fr.chef_text;

  return (
    <main className="min-h-screen bg-[#0a0a0a] pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 px-6 md:px-12">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={heroImage}
            alt="BÉNI Restaurant"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">
              {get('about_subtitle_short')}
            </p>
            <h1 className="font-display text-5xl md:text-7xl text-white mb-6" data-testid="about-title">
              {get('about_title')}
            </h1>
            <p className="font-accent text-xl text-white/70 italic">
              {get('about_intro')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-20 px-6 md:px-12" data-testid="history-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="font-display text-3xl md:text-4xl text-white">
                {get('about_history_title')}
              </h2>
              <p className="text-white/70 leading-relaxed">
                {get('about_history_p1')}
              </p>
              <p className="text-white/70 leading-relaxed">
                {get('about_history_p2')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src={historyImage}
                alt="BÉNI Interior"
                className="w-full h-auto max-h-[500px] object-contain"
                data-testid="history-image"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 border-2 border-[#d4af37]" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Chef Section */}
      <section className="py-20 px-6 md:px-12 bg-[#121212]" data-testid="chef-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <img
                src={chefImage}
                alt="Chef Stephano Crupi"
                className="w-full h-auto max-h-[600px] object-contain"
                data-testid="chef-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6 order-1 lg:order-2"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37]">
                {get('about_chef_title')}
              </p>
              <h2 className="font-display text-3xl md:text-4xl text-white">
                {chefName}
              </h2>
              <p className="text-white/70 leading-relaxed">
                {chefIntro} <span className="text-[#d4af37] font-medium">{chefName}</span> {chefText}
              </p>
              
              <blockquote className="border-l-2 border-[#d4af37] pl-6 py-4 my-8">
                <Quote className="w-8 h-8 text-[#d4af37]/30 mb-4" />
                <p className="font-accent text-lg italic text-white/60">
                  {get('about_quote')}
                </p>
              </blockquote>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 px-6 md:px-12" data-testid="philosophy-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37]">
              {get('about_philosophy_title')}
            </p>
            <p className="text-white/70 text-lg leading-relaxed max-w-2xl mx-auto">
              {get('about_philosophy_text')}
            </p>
            
            <Link
              to="/menu"
              className="inline-flex items-center gap-3 bg-[#d4af37] text-black px-10 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-all duration-300 mt-8"
              data-testid="about-menu-cta"
            >
              {get('about_cta')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Image Grid */}
      <section className="py-20 px-6 md:px-12 bg-[#121212]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(galleryImages.length > 0 ? galleryImages.map(img => img.url) : defaultGalleryImages).map((src, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <img
                  src={src}
                  alt={`BÉNI dish ${index + 1}`}
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  data-testid={`about-gallery-${index}`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
